import { useCallback, useMemo, useState } from 'react';

import { UsersIcon } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import SearchBar from '@/components/atoms/SearchBar/SearchBar';
import {
  MODAL_ACCOUNT_TRANSFER,
  MODAL_DELETE,
  MODAL_ROLE_EDIT,
} from '@/components/organisms/Modals/ModalConductor';
import { SettingsContent } from '@/components/templates/SettingsContent/SettingsContent';
import { SimpleFilterCombobox } from '@/components/ui/custom/SimpleFilterCombobox';
import { DataTable } from '@/components/ui/table/data-table';
import { useAccount } from '@/hooks/useAccount';
import { useInvitations } from '@/hooks/useInvitations';
import useMembers from '@/hooks/useMembers';
import { useRoles } from '@/hooks/useRoles';
import { useTeams } from '@/hooks/useTeams';
import useUser from '@/hooks/useUser';
import { popModal, pushModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { trackEvent } from '@/segment/segment';
import useSegments from '@/segment/useSegments';
import { RoleType } from '@/shared/types/member';
import { actions } from '@/shared/types/permissions';
import { EventName } from '@/shared/types/segment';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { greekAwareTextMatch } from '@/util/greekFilter';
import { getInvitationRoleIds } from '@/util/memberUtils';

import { createColumns, MemberOrInvitation } from './columns';

/**
 * ManageAccessMembers page component that displays and manages team members and invitations.
 *
 * Features:
 * - Displays members and pending invitations in a sortable table
 * - Provides role-based filtering (all, no role, pending)
 * - Includes search functionality by name or email
 * - Handles member role editing, account transfer, and deletion
 * - Manages invitation deletion and role assignment
 * - Shows permission-based access control for actions
 */
export const ManageAccessMembers = () => {
  useSegments(PageName.MANAGE_MEMBER_ACCESS);

  // Hooks
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useUser();
  const { members, deleteMember } = useMembers();
  const { teams } = useTeams();
  const { roles } = useRoles();
  const { accountRole } = useAccount();
  const { accountInvitations, deleteInvitation } = useInvitations();

  // Selectors
  const canAssign = useSelector((state: RootState) =>
    getPermissions(state, 'roles', actions.ASSIGN)
  );

  // State for filtering and search
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [searchValue, setSearchValue] = useState<string>('');

  // Filter options for role-based filtering
  const filterOptions = useMemo(
    () => [
      { label: t('roles.filters.all'), value: 'all' },
      { label: t('roles.filters.no_role'), value: 'no_role' },
      { label: t('roles.filters.pending'), value: 'pending' },
    ],
    [t]
  );

  // Filter data based on role filter and search
  const filteredData = useMemo(() => {
    const formattedAccountInvitations =
      accountInvitations?.map((inv) => ({
        ...inv,
        name: inv?.email,
        role_ids: getInvitationRoleIds(inv),
      })) ?? [];

    let filtered = [];

    if (selectedFilter === 'all') {
      filtered = [...formattedAccountInvitations, ...(members || [])];
    } else if (selectedFilter === 'no_role') {
      const updatedInvitations = formattedAccountInvitations?.filter(
        (r) => r.role_ids && r.role_ids.length === 0
      );
      const formattedMembers = members?.filter(
        (m) => m.role_ids && m.role_ids.length === 0 && m.role !== RoleType.OWNER
      );
      filtered = [...updatedInvitations, ...formattedMembers];
    } else {
      filtered = [...formattedAccountInvitations];
    }

    if (searchValue) {
      filtered = filtered.filter((item) => {
        // Check name field with Greek-aware filtering
        const nameMatch = item.name ? greekAwareTextMatch(item.name, searchValue) : false;

        // Check email field with simple case-insensitive matching
        const emailMatch = item.email
          ? item.email.toLowerCase().includes(searchValue.toLowerCase())
          : false;

        return nameMatch || emailMatch;
      });
    }

    return filtered;
  }, [accountInvitations, members, selectedFilter, searchValue]);

  // Action handlers
  const handleEditRole = useCallback(
    (selectedUser: MemberOrInvitation) => {
      trackEvent(EventName.ClickEditMember);
      dispatch(pushModal(MODAL_ROLE_EDIT, { user: selectedUser }));
    },
    [dispatch]
  );

  const handleTransferAccount = useCallback(
    (selectedUser: MemberOrInvitation) => {
      trackEvent(EventName.ClickTransferAccount);
      dispatch(pushModal(MODAL_ACCOUNT_TRANSFER, { user_id: selectedUser?.user_id }));
    },
    [dispatch]
  );

  const handleDeleteMember = useCallback(
    (selectedUser: MemberOrInvitation) => {
      if (!('invitation_code' in selectedUser)) {
        trackEvent(EventName.ClickDeleteMember);

        const deleteProps = {
          subtitle: <Trans i18nKey="members.delete_subtitle" values={[selectedUser?.name]} />,
          title: t('members.delete'),
          confirm: true,
          onDelete: () => {
            deleteMember(
              { user_id: selectedUser?.user_id },
              {
                onSuccess: () => {
                  dispatch(popModal());
                },
              }
            );
          },
        };
        dispatch(pushModal(MODAL_DELETE, deleteProps));
      } else {
        trackEvent(EventName.ClickDeleteInvitation);

        const deleteProps = {
          subtitle: <Trans i18nKey="invitations.delete_warn" values={[selectedUser?.email]} />,
          title: t('invitations.revoke'),
          onDelete: () => {
            deleteInvitation(
              { email: selectedUser?.email },
              {
                onSuccess: () => {
                  dispatch(popModal());
                },
              }
            );
          },
        };
        dispatch(pushModal(MODAL_DELETE, deleteProps));
      }
    },
    [deleteInvitation, deleteMember, dispatch, t]
  );

  // Create columns with action handlers
  const columns = useMemo(
    () =>
      createColumns({
        roles,
        teams,
        user,
        accountRole,
        canAssign,
        onEditRole: handleEditRole,
        onTransferAccount: handleTransferAccount,
        onDeleteMember: handleDeleteMember,
      }),
    [
      roles,
      teams,
      user,
      accountRole,
      canAssign,
      handleEditRole,
      handleTransferAccount,
      handleDeleteMember,
    ]
  );

  if (!members) {
    return null;
  }

  return (
    <SettingsContent fill className="pt-5">
      <div className="mb-4 flex items-center justify-between gap-2 pl-1">
        <SearchBar
          placeholder={t('members.search_placeholder')}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />

        <SimpleFilterCombobox
          options={filterOptions}
          value={selectedFilter}
          onChange={setSelectedFilter}
          icon={<UsersIcon />}
        />
      </div>

      <DataTable.Root
        fill
        className="min-h-0 flex-1"
        columns={columns}
        data={filteredData}
        rowCount={filteredData.length}
        resourceName="member"
        emptyStateMessage={t('members.no_members')}
      >
        <DataTable.Content />
        <DataTable.Pagination />
      </DataTable.Root>
    </SettingsContent>
  );
};
